// @ts-nocheck
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpDown,
  Brain,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  SquarePlay,
} from "lucide-react";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { getCategoryLabel } from "../../constants/kpiCategories";
import {
  useHawkeyeSorting,
  HawkeyeSortField,
} from "../../hooks/hawkeye/useHawkeyeSorting";
import { useIsMobile } from "../../hooks/use-mobile";
import {
  useHawkeyeMetrics,
  type HawkeyeMetricsAccountsInput,
} from "../../hooks/useHawkeyeMetrics";
import { useKpiDatasetKind } from "../../../Hooks/useKpiDatasetKind";
import { useScrollChainRef } from "../../../Hooks/useScrollChain";
import useXFactorKpiAccountBriefing from "../../../Hooks/useXFactorKpiAccountBriefing";
import useXFactorKpiFullBriefing from "../../../Hooks/useXFactorKpiFullBriefing";
import useXFactorKpiMatrix from "../../../Hooks/useXFactorKpiMatrix";
import { useAppDispatch } from "../../../Store/Hooks";
import { fetchXFactorKpiAccountBriefings } from "../../../Store/Reducers/XFactorBriefingSlice";
import type { XFactorSectionBubble } from "../../../Types/XFactorViewsRendering";
import { formatIsoDateMonDayYear } from "../../../Utils/Helper/Helper";

import BriefingMatrixSkeleton from "../../../Container/WatchlistSummary/BriefingMatrixSkeleton";
import {
  ACCOUNT_COL_PX,
  ACTION_COL_PX,
  railStyle,
} from "../../../Pages/TempViewsRendering/components/BubbleMatrix";
import {
  ACCENT,
  Bubble,
  Modal,
} from "../../../Pages/TempViewsRendering/components/Chrome";
import KpiCategoryDetailModal from "../../../Pages/TempViewsRendering/components/KpiCategoryDetailModal";
import KpiExecutiveBrief from "../../../Pages/TempViewsRendering/components/KpiExecutiveBrief";
import KpiFullBriefingModal from "../../../Pages/TempViewsRendering/components/KpiFullBriefingModal";
import KpiSituationCard from "../../../Pages/TempViewsRendering/components/KpiSituationCard";
import { bandOf } from "../../../Pages/TempViewsRendering/lib/derive";
import KpiCompareSelectionStrip from "./KpiCompareSelectionStrip";
import KpiCompareMissingDataModal, {
  getSelectedAccountsWithoutKpiData,
} from "./KpiCompareMissingDataModal";
import SpiderCompareModalContent from "./SpiderCompareModalContent";

/** Pull overall synopsis from a kpi/account-briefings category payload. */
const synopsisFromBriefing = (raw: unknown): string | null => {
  if (!raw || typeof raw !== "object") return null;
  const root = raw as Record<string, unknown>;
  let briefing = root.briefing;
  if (
    briefing &&
    typeof briefing === "object" &&
    (briefing as Record<string, unknown>).briefing &&
    typeof (briefing as Record<string, unknown>).briefing === "object"
  ) {
    briefing = (briefing as Record<string, unknown>).briefing;
  }
  const b =
    briefing && typeof briefing === "object"
      ? (briefing as Record<string, unknown>)
      : root;
  const overall =
    (b.overall && typeof b.overall === "object" ? b.overall : null) ||
    (b.category && typeof b.category === "object" ? b.category : null);
  if (!overall || typeof overall !== "object") return null;
  const syn = (overall as Record<string, unknown>).synopsis;
  return typeof syn === "string" && syn.trim() ? syn.trim() : null;
};
type HawkeyeDataSource = "hawkeye" | "digitalocean";

const OVERALL_ID = "overall";
/**
 * Overall carries the whole weight: 20% × 5 = 100 → full H diameter.
 * Matches XfactorKpiMatrix / xfactor sizeBand(weight% × 5).
 */
const OVERALL_WEIGHT_SHARE = 20;

/**
 * Account Health Chrome Bubble — same rule as XfactorKpiMatrix:
 * Colour = health score band; size = category weight share × 5 (L/M/H).
 * Size is never the score (that made hover Health and Score show the same number).
 */
const toMatrixBubble = (
  sectionId: string,
  label: string,
  score: number | null,
  weightSharePct: number | null
): XFactorSectionBubble => {
  const scaled = weightSharePct == null ? null : weightSharePct * 5;
  return {
    sectionId,
    label,
    score,
    band: bandOf(score),
    size: scaled == null ? 0 : Math.min(1, scaled / 100),
    relevance: scaled,
    synopsis: null,
    riskCount: 0,
    opportunityCount: 0,
    risk: null,
    opportunity: null,
    confidence: null,
    missing: score == null,
  };
};

const pctText = (n: number | null) =>
  n == null ? "—" : `${Number(n.toFixed(1))}%`;

interface HawkeyeMatrixBubbleChartProps {
  selectedKPIs?: string[];
  measurementDate?: string;
  metricsAccountsInput?: HawkeyeMetricsAccountsInput;
  hawkeyeMetrics: HawkeyeChartMetricsProps;
  highlightedAccountIds?: string[];
  onHighlightedAccountIdsChange?: (accountIds: string[]) => void;
  onSaveAsWatchlist?: (accountIds: string[]) => void;
  selectionPanelRef?: React.RefObject<HTMLElement | null>;
  /** When false, only show accounts with no KPI data. */
  showUnavailableKpiData?: boolean;
  /**
   * Progressive row/dot reveal. Keep true for the first Always On page;
   * later pages should show rows immediately.
   */
  enableRowReveal?: boolean;
  /** Fired once when progressive reveal finishes (or is skipped). */
  onRevealComplete?: () => void;
  /**
   * When set, the selection strip portals here (below pagination in Always On).
   */
  selectionStripHost?: HTMLElement | null;
  dataSource?: HawkeyeDataSource;
}

const areAccountSelectionsEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((accountId, index) => accountId === right[index]);

export const HawkeyeMatrixBubbleChart: React.FC<
  HawkeyeMatrixBubbleChartProps
> = ({
  selectedKPIs,
  measurementDate,
  metricsAccountsInput,
  hawkeyeMetrics,
  highlightedAccountIds = [],
  onHighlightedAccountIdsChange,
  onSaveAsWatchlist,
  selectionPanelRef,
  showUnavailableKpiData = true,
  enableRowReveal = true,
  onRevealComplete,
  selectionStripHost = null,
  dataSource = "hawkeye",
}) => {
  const isDigitalocean = dataSource === "digitalocean";
  const MIN_COMPARE_ACCOUNTS = 2;
  const COMPARE_COLORS = ["#7c3aed", "#2563eb", "#0d9488", "#d97706"];
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [categoryModalData, setCategoryModalData] = useState<{
    accountData: any;
    categoryName: string;
    categoryScore: number;
    accountIndex: number;
    categoryIndex: number;
    elementRect: DOMRect | null;
  } | null>(null);
  const [overallModalData, setOverallModalData] = useState<{
    accountData: any;
    accountIndex: number;
  } | null>(null);
  const [nativeDetail, setNativeDetail] = useState<{
    accountId: string;
    categoryId: string;
    /** Opened from Action → Full Briefing (title uses Full Briefing, not Overall). */
    fromFullBriefing?: boolean;
  } | null>(null);
  /** Action column → Situation Card (same as Account Synopsis Account Health). */
  const [situationAccount, setSituationAccount] = useState<{
    accountId: string;
    accountName: string;
  } | null>(null);
  const [situationCardView, setSituationCardView] = useState<
    "situation" | "brief"
  >("situation");
  /** Business Health–style expandable one-line summary under each row. */
  const [summaryOverrides, setSummaryOverrides] = useState<
    Record<string, boolean>
  >({});
  const [kpiSynopsisByAccountId, setKpiSynopsisByAccountId] = useState<
    Record<string, string>
  >({});
  const isMobile = useIsMobile();
  const datasetKind = useKpiDatasetKind();
  const dispatch = useAppDispatch();

  // Progressive reveal state
  const [visibleRowCount, setVisibleRowCount] = useState(0);
  const [showBuildingModal, setShowBuildingModal] = useState(false);
  const [modalProgress, setModalProgress] = useState(0);
  const prevLoadingRef = useRef(false);
  const revealTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const graceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevMeasurementDateRef = useRef(measurementDate);
  const hasShownInitialModal = useRef(false);
  const revealCompleteNotifiedRef = useRef(false);
  const onRevealCompleteRef = useRef(onRevealComplete);
  onRevealCompleteRef.current = onRevealComplete;
  const setMatrixScrollChainRef = useScrollChainRef<HTMLDivElement>();

  const notifyRevealComplete = () => {
    if (revealCompleteNotifiedRef.current) return;
    revealCompleteNotifiedRef.current = true;
    onRevealCompleteRef.current?.();
  };

  // Expand/collapse synopsis state
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(
    new Set(),
  );
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedCompareAccountIds, setSelectedCompareAccountIds] = useState<
    string[]
  >([]);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
  const [isMissingKpiDataModalOpen, setIsMissingKpiDataModalOpen] =
    useState(false);
  const [compareSelectionMessage, setCompareSelectionMessage] = useState<
    string | null
  >(null);

  useEffect(() => {
    setSelectedCompareAccountIds(prevIds =>
      areAccountSelectionsEqual(prevIds, highlightedAccountIds)
        ? prevIds
        : highlightedAccountIds
    );
  }, [highlightedAccountIds]);

  useEffect(() => {
    if (selectedCompareAccountIds.length === 0) {
      setCompareSelectionMessage(null);
      return;
    }
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    setCompareSelectionMessage(null);
  }, [selectedCompareAccountIds.length]);

  // Listen for configure metrics event from header
  React.useEffect(() => {
    const handleConfigureMetrics = () => {
      setIsConfigModalOpen(true);
    };

    window.addEventListener("hawkeyeConfigureMetrics", handleConfigureMetrics);
    return () =>
      window.removeEventListener(
        "hawkeyeConfigureMetrics",
        handleConfigureMetrics,
      );
  }, []);

  const {
    matrixData,
    loading,
    error,
    configuration,
    updateConfiguration,
    availableCategories,
    orderedCategories,
    refetch,
    reloadConfiguration,
  } = hawkeyeMetrics;

  const { sortedData, sortConfig, handleSort } = useHawkeyeSorting(
    matrixData,
    highlightedAccountIds,
    showUnavailableKpiData
  );

  /** Native xfactor view=table scores — same source as XfactorKpiMatrix / click modal. */
  const nativeAccountIds = useMemo(
    () =>
      isDigitalocean
        ? sortedData.map(a => String(a.account_id || "").trim()).filter(Boolean)
        : [],
    [isDigitalocean, sortedData]
  );
  const {
    columns: nativeColumns,
    rowsByAccountId: nativeRowsByAccountId,
  } = useXFactorKpiMatrix(
    nativeAccountIds,
    isDigitalocean ? measurementDate || null : null
  );

  const nativeAccountIndex = nativeDetail
    ? sortedData.findIndex(
        a => String(a.account_id) === String(nativeDetail.accountId)
      )
    : -1;
  const nativeAccountName =
    nativeAccountIndex >= 0
      ? sortedData[nativeAccountIndex].account_name
      : "";
  const {
    briefing: nativeBriefing,
    loading: nativeBriefingLoading,
    error: nativeBriefingError,
  } = useXFactorKpiFullBriefing(
    isDigitalocean ? nativeDetail?.accountId : null,
    // Same period as the matrix (null → newest briefing ≠ hover score).
    measurementDate || null
  );

  const {
    data: kpiSituationData,
    date: kpiSituationDate,
    loading: kpiSituationLoading,
  } = useXFactorKpiAccountBriefing(
    situationAccount?.accountId,
    measurementDate || undefined
  );

  const openSituationCard = (account: {
    account_id?: string;
    account_name?: string;
    hasData?: boolean;
  }) => {
    const accountId = String(account.account_id || "").trim();
    if (!accountId || !account.hasData) return;
    setSituationCardView("situation");
    setSituationAccount({
      accountId,
      accountName: account.account_name || "Unknown account",
    });
  };

  const openFullBriefing = (account: {
    account_id?: string;
    account_name?: string;
    hasData?: boolean;
  }) => {
    const accountId = String(account.account_id || "").trim();
    if (!accountId || !account.hasData || !isDigitalocean) return;
    setNativeDetail({
      accountId,
      categoryId: OVERALL_ID,
      fromFullBriefing: true,
    });
  };

  // Track loading transitions
  useEffect(() => {
    if (!enableRowReveal || revealCompleteNotifiedRef.current) {
      setShowBuildingModal(false);
      setVisibleRowCount(sortedData.length);
      if (sortedData.length) notifyRevealComplete();
      return;
    }

    const dateChanged = prevMeasurementDateRef.current !== measurementDate;
    prevMeasurementDateRef.current = measurementDate;

    // Cancel any pending grace timer when loading state changes
    if (graceTimerRef.current) {
      clearTimeout(graceTimerRef.current);
      graceTimerRef.current = null;
    }

    if (loading) {
      const isFirstLoad =
        !hasShownInitialModal.current && sortedData.length === 0;
      const isMeaningfulChange = dateChanged;

      if (isFirstLoad || isMeaningfulChange) {
        // Grace period: if data arrives within 150ms (cache hit), skip the modal
        graceTimerRef.current = setTimeout(() => {
          setShowBuildingModal(true);
          setVisibleRowCount(0);
          setModalProgress(0);
          hasShownInitialModal.current = true;
          if (revealTimerRef.current) {
            clearInterval(revealTimerRef.current);
            revealTimerRef.current = null;
          }
        }, 150);
      }
    }

    if (!loading && prevLoadingRef.current) {
      // Data just arrived
      if (graceTimerRef.current) {
        // Cache hit — data arrived before grace period expired, skip modal entirely
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
        // Show rows instantly
        setVisibleRowCount(sortedData.length);
      } else if (showBuildingModal) {
        // Loader was the full skeleton until digest completed; close immediately so the
        // "building" dialog does not appear only after loading finishes.
        setShowBuildingModal(false);
        setVisibleRowCount(sortedData.length);
      }
    }

    prevLoadingRef.current = loading;

    return () => {
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    };
  }, [
    loading,
    measurementDate,
    sortedData.length,
    showBuildingModal,
    enableRowReveal,
  ]);

  // Animate modal progress bar
  useEffect(() => {
    if (!enableRowReveal || !showBuildingModal) return;
    const interval = setInterval(() => {
      setModalProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(interval);
  }, [showBuildingModal, enableRowReveal]);

  // Derived step progress values
  const step1Progress = Math.min(100, Math.max(0, (modalProgress / 33) * 100));
  const step2Progress = Math.min(
    100,
    Math.max(0, ((modalProgress - 33) / 33) * 100),
  );
  const step3Progress = Math.min(
    100,
    Math.max(0, ((modalProgress - 66) / 34) * 100),
  );

  // Progressive row reveal timer
  useEffect(() => {
    if (!enableRowReveal || revealCompleteNotifiedRef.current) {
      setVisibleRowCount(sortedData.length);
      if (sortedData.length) notifyRevealComplete();
      return;
    }
    if (loading || showBuildingModal) return;
    if (!sortedData.length || visibleRowCount >= sortedData.length) {
      if (sortedData.length && visibleRowCount >= sortedData.length) {
        notifyRevealComplete();
      }
      return;
    }

    revealTimerRef.current = setInterval(() => {
      setVisibleRowCount((prev) => {
        const next = prev + 1;
        if (next >= sortedData.length) {
          if (revealTimerRef.current) clearInterval(revealTimerRef.current);
          revealTimerRef.current = null;
          notifyRevealComplete();
        }
        return next;
      });
    }, 200);

    return () => {
      if (revealTimerRef.current) {
        clearInterval(revealTimerRef.current);
        revealTimerRef.current = null;
      }
    };
  }, [
    loading,
    showBuildingModal,
    sortedData.length,
    visibleRowCount,
    enableRowReveal,
  ]);

  // Use the ordered categories from the hook
  const displayCategories = orderedCategories;

  /**
   * Weight shares for size — prefer xfactor table `effectiveWeightPct` when
   * digitalocean (same as XfactorKpiMatrix); else org config weights.
   */
  const weightByCategory = useMemo(() => {
    const out: Record<string, number> = {};
    if (isDigitalocean && nativeColumns.length) {
      for (const col of nativeColumns) {
        if (col.categoryId && col.weight != null && Number.isFinite(col.weight)) {
          out[col.categoryId] = col.weight;
        }
      }
      return out;
    }
    for (const category of displayCategories) {
      const w = Number(configuration.categoryWeights?.[category] ?? 0);
      if (Number.isFinite(w) && w > 0) out[category] = w;
    }
    return out;
  }, [
    isDigitalocean,
    nativeColumns,
    displayCategories,
    configuration.categoryWeights,
  ]);

  const totalCategoryWeight = useMemo(
    () =>
      Object.values(weightByCategory).reduce(
        (sum, w) => sum + (Number.isFinite(w) && w > 0 ? w : 0),
        0
      ),
    [weightByCategory]
  );
  const shareOf = (category: string): number | null => {
    const w = weightByCategory[category];
    if (w == null || !Number.isFinite(w) || w <= 0 || !totalCategoryWeight)
      return null;
    return (w / totalCategoryWeight) * 100;
  };

  const selectedCompareAccounts = useMemo(
    () =>
      sortedData.filter((account) =>
        selectedCompareAccountIds.includes(account.account_id),
      ),
    [sortedData, selectedCompareAccountIds],
  );

  const selectedAccountsWithoutKpiData = useMemo(
    () => getSelectedAccountsWithoutKpiData(selectedCompareAccounts),
    [selectedCompareAccounts]
  );

  const handleCompareClick = () => {
    if (selectedCompareAccountIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage("Select at least 2 accounts to compare.");
      return;
    }
    if (selectedAccountsWithoutKpiData.length > 0) {
      setIsMissingKpiDataModalOpen(true);
      return;
    }
    setIsCompareModalOpen(true);
  };

  const handleRemoveAccountsWithoutDataAndCompare = () => {
    const nextIds = selectedCompareAccounts
      .filter(account => account.hasData !== false)
      .map(account => String(account.account_id));

    setIsMissingKpiDataModalOpen(false);
    setSelectedCompareAccountIds(nextIds);
    onHighlightedAccountIdsChange?.(nextIds);

    if (nextIds.length < MIN_COMPARE_ACCOUNTS) {
      setCompareSelectionMessage(
        `At least ${MIN_COMPARE_ACCOUNTS} accounts with KPIs data are required to compare.`
      );
      return;
    }

    setIsCompareModalOpen(true);
  };

  const toggleCompareSelection = (accountId: string) => {
    setSelectedCompareAccountIds((prev) => {
      let nextIds = prev;
      if (prev.includes(accountId)) {
        if (isCompareModalOpen && prev.length <= MIN_COMPARE_ACCOUNTS) {
          setCompareSelectionMessage(
            "At least 2 accounts are required in comparison."
          );
          return prev;
        }
        nextIds = prev.filter((id) => id !== accountId);
      } else {
        nextIds = [...prev, accountId];
      }

      onHighlightedAccountIdsChange?.(nextIds);
      return nextIds;
    });
  };

  // Minimum widths unused after Business Health table markup — kept for
  // reference if Metrics view still needs them via sibling chart.
  const getCategoryColumnWidth = (category: string) => {
    if (category === "health") {
      return isMobile ? "64px" : "72px";
    }
    if (category === "adoption") {
      return isMobile ? "84px" : "88px";
    }
    if (category === "support") {
      return isMobile ? "88px" : "96px";
    }
    if (category === "relationships") {
      return isMobile ? "92px" : "100px";
    }
    return isMobile ? "64px" : "68px";
  };

  void getCategoryColumnWidth;

  const headerColumns = useMemo(
    () => [
      { id: OVERALL_ID, label: "Overall", sortField: "overallScore" as HawkeyeSortField },
      ...displayCategories.map(category => ({
        id: category,
        label: getCategoryLabel(category),
        sortField: category as HawkeyeSortField,
      })),
    ],
    [displayCategories]
  );

  const accountSummary = (account: (typeof sortedData)[number]): string | null => {
    const fromKpi = kpiSynopsisByAccountId[String(account.account_id)]?.trim();
    if (fromKpi) return fromKpi;
    const fromDigest = account.aiCategoryAssessments?.overall?.trim();
    return fromDigest || null;
  };

  const isSummaryOpen = (accountId: string, index: number) =>
    summaryOverrides[accountId] ?? index === 0;

  const toggleSummary = (accountId: string, currentlyOpen: boolean) =>
    setSummaryOverrides(s => ({
      ...s,
      [accountId]: !currentlyOpen,
    }));

  const synopsisAccountIdsKey = useMemo(
    () =>
      sortedData
        .filter(a => a.hasData)
        .map(a => String(a.account_id || "").trim())
        .filter(Boolean)
        .join(","),
    [sortedData]
  );

  // Batch-load KPI overall synopsis for digitalocean Account Health (BH parity).
  useEffect(() => {
    if (!isDigitalocean) {
      setKpiSynopsisByAccountId({});
      return;
    }
    const ids = synopsisAccountIdsKey
      ? synopsisAccountIdsKey.split(",").filter(Boolean)
      : [];
    if (!ids.length) {
      setKpiSynopsisByAccountId({});
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await dispatch(
          fetchXFactorKpiAccountBriefings({
            accountIds: ids,
            view: "category",
            categoryId: "overall",
            datasetKind,
            measurementDate,
          })
        ).unwrap();
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const row of res?.results ?? []) {
          const accountId = String(row?.account_id || "").trim();
          if (!accountId) continue;
          const syn = synopsisFromBriefing(row?.briefing);
          if (syn) next[accountId] = syn;
        }
        setKpiSynopsisByAccountId(next);
      } catch {
        if (!cancelled) setKpiSynopsisByAccountId({});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    isDigitalocean,
    datasetKind,
    measurementDate,
    synopsisAccountIdsKey,
  ]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    setScrollPosition(e.currentTarget.scrollLeft);
  };

  const sortMarker = (field: HawkeyeSortField) => {
    if (!sortConfig || sortConfig.field !== field) return "";
    return sortConfig.direction === "asc" ? " ↑" : " ↓";
  };

  const handleBubbleClick = (
    accountData: any,
    categoryName: string,
    categoryScore: number,
    event?: React.MouseEvent | null,
  ) => {
    if (!accountData?.hasData) return;
    if (isDigitalocean) {
      setNativeDetail({
        accountId: String(accountData.account_id || "").trim(),
        categoryId: categoryName || "overall",
      });
      return;
    }
    const accountIndex = sortedData.findIndex(
      (acc) => acc.account_id === accountData.account_id,
    );
    const categoryIndex = displayCategories.findIndex(
      (cat) => cat === categoryName,
    );
    const elementRect = event?.currentTarget
      ? (event.currentTarget as HTMLElement).getBoundingClientRect()
      : null;

    setCategoryModalData({
      accountData,
      categoryName,
      categoryScore,
      accountIndex,
      categoryIndex,
      elementRect,
    });
  };

  const handleModalNavigation = (
    accountIndex: number,
    categoryIndex: number,
  ) => {
    const newAccount = sortedData[accountIndex];
    const newCategory = displayCategories[categoryIndex];
    const newCategoryData = newAccount.categoryScores.find(
      (cs: any) => cs.category === newCategory,
    );
    const newScore = newCategoryData?.score;

    // Sequential update: set data immediately, let React handle the render
    setCategoryModalData({
      accountData: newAccount,
      categoryName: newCategory,
      categoryScore: newScore,
      accountIndex,
      categoryIndex,
      elementRect: null,
    });
  };

  const handleCloseCategoryModal = () => {
    setCategoryModalData(null);
  };

  const handleOverallBubbleClick = (account: any, _event?: React.MouseEvent) => {
    if (!account?.hasData) return;
    if (isDigitalocean) {
      setNativeDetail({
        accountId: String(account.account_id || "").trim(),
        categoryId: "overall",
      });
      return;
    }
    const accountIndex = sortedData.findIndex(
      (acc) => acc.account_id === account.account_id,
    );
    setOverallModalData({ accountData: account, accountIndex });
  };

  const handleOverallModalNavigation = (accountIndex: number) => {
    const newAccount = sortedData[accountIndex];
    setOverallModalData({ accountData: newAccount, accountIndex });
  };

  const handleCloseOverallModal = () => {
    setOverallModalData(null);
  };

  // Cross-modal navigation: Overall → Category (right nav)
  const handleNavigateFromOverallToCategory = (accountIndex: number) => {
    const account = sortedData[accountIndex];
    const firstCategory = displayCategories[0];
    const catData = account.categoryScores.find(
      (cs: any) => cs.category === firstCategory,
    );
    setOverallModalData(null);
    setCategoryModalData({
      accountData: account,
      categoryName: firstCategory,
      categoryScore: catData?.score,
      accountIndex,
      categoryIndex: 0,
      elementRect: null,
    });
  };

  // Cross-modal navigation: Category → Overall (left nav from index 0)
  const handleNavigateFromCategoryToOverall = (accountIndex: number) => {
    const account = sortedData[accountIndex];
    setCategoryModalData(null);
    setOverallModalData({ accountData: account, accountIndex });
  };

  // Expand/collapse helpers — any account with visible data can expand
  const expandableAccounts = useMemo(
    () => sortedData.filter((a) => a.hasData),
    [sortedData],
  );

  const toggleAccountExpand = (accountId: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const toggleAllExpanded = () => {
    if (allExpanded) {
      setExpandedAccounts(new Set());
      setAllExpanded(false);
    } else {
      setExpandedAccounts(new Set(expandableAccounts.map((a) => a.account_id)));
      setAllExpanded(true);
    }
  };

  // useHawkeyeMetrics keeps `loading` true until hawkeye-digest-db returns; metrics can
  // populate sortedData first, which used to clear this flag early and hide the loader.
  const hasMechanicalGridContent = sortedData.some(
    (account) =>
      account.hasData || (account.rawMetrics && account.rawMetrics.length > 0),
  );
  const awaitingDigestWhileMetricsLoaded =
    loading && sortedData.length > 0 && hasMechanicalGridContent;

  const isInitialLoadingState =
    (loading &&
      (sortedData.length === 0 ||
        sortedData.every(
          (account) =>
            !account.hasData &&
            (!account.rawMetrics || account.rawMetrics.length === 0),
        ))) ||
    awaitingDigestWhileMetricsLoaded;

  if (isInitialLoadingState) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden px-1">
        <BriefingMatrixSkeleton
          sections={displayCategories.map(c => ({
            id: c,
            name: getCategoryLabel(c),
          }))}
          caption="Loading Account Health…"
          ariaLabel="Loading Account Health"
          showActions
        />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="m-6">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2 text-destructive">
            Error Loading Metrics
          </h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={refetch} variant="outline" className="mt-2">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!sortedData.length) {
    return (
      <Card className="m-6">
        <CardContent className="p-6 text-center">
          <h2 className="text-xl font-semibold mb-2">No Metrics Data</h2>
          <p className="text-muted-foreground">
            No metrics data available for the selected accounts and date range.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* AI Building Modal */}
      <Dialog
        open={
          showBuildingModal && sortedData.length > 0 && !isInitialLoadingState
        }
      >
        <DialogContent
          className="sm:max-w-2xl rounded-2xl border-none bg-gradient-to-br from-purple-50/95 via-white/95 to-violet-50/95 backdrop-blur-sm shadow-[0_0_60px_hsl(270_60%_70%/0.25)] [&>button]:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center gap-6 py-6 text-center">
            {/* Pulsing Brain Icon with Multi-Ring Spinner */}
            <div className="relative w-20 h-20 flex items-center justify-center">
              <div
                className="absolute inset-0 w-20 h-20 border-[3px] border-transparent border-t-violet-300 rounded-full animate-spin"
                style={{ animationDuration: "2.5s" }}
              />
              <div
                className="absolute inset-2 w-16 h-16 border-[2.5px] border-transparent border-r-violet-200 rounded-full animate-spin"
                style={{
                  animationDuration: "1.8s",
                  animationDirection: "reverse",
                }}
              />
              <div
                className="absolute inset-4 w-12 h-12 border-2 border-transparent border-b-violet-100 rounded-full animate-spin"
                style={{ animationDuration: "1.2s" }}
              />
              <Brain className="h-8 w-8 text-violet-500 animate-pulse relative z-10" />
            </div>

            {/* Title */}
            <h3 className="text-sm font-semibold tracking-[0.3em] text-violet-600 uppercase">
              Generating Intelligence Briefings From Enterprise KPIs
            </h3>

            {/* Overall Progress Bar */}
            <div className="w-full px-4">
              <div className="w-full h-2.5 rounded-full bg-violet-100/60 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-violet-400 to-violet-500 transition-all duration-100 ease-linear"
                  style={{ width: `${modalProgress}%` }}
                />
              </div>
            </div>

            {/* 3 Progression Columns */}
            <div className="grid grid-cols-3 gap-4 w-full px-4">
              {[
                {
                  label: "Gathering and Analyzing Your Enterprise KPIs...",
                  progress: step1Progress,
                },
                {
                  label: "Computing Normalized and Benchmarked Metrics...",
                  progress: step2Progress,
                },
                {
                  label: "Organizing Actionable Insights...",
                  progress: step3Progress,
                },
              ].map((step, i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`w-2 h-2 rounded-full ${step.progress > 0 ? "bg-amber-400" : "bg-gray-300"} transition-colors`}
                    />
                    <span className="text-[10px] font-medium tracking-wider text-muted-foreground uppercase leading-tight text-center">
                      {step.label}
                    </span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-violet-100/60 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-violet-300 transition-all duration-100 ease-linear"
                      style={{ width: `${step.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Matrix Container — Business Health table markup (commit 6663c986) */}
      <div
        className="relative isolate z-0 flex h-full min-h-0 flex-1 flex-col"
        style={
          selectedCompareAccountIds.length > 0 && !selectionStripHost
            ? { marginBottom: "4.5rem" }
            : undefined
        }
      >
        <div
          ref={setMatrixScrollChainRef}
          className="scrollbar-hover min-h-0 flex-1 overflow-auto overscroll-contain px-1 pb-2"
          onScroll={handleScroll}
        >
          <div className="watchlist-summary-matrix watchlist-summary-matrix--embed matrix-stagger-suppressed">
            <table className="w-full min-w-[1180px] table-fixed border-separate border-spacing-0 text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th
                    className="sticky top-0 left-0 z-40 bg-[#f1f5f9] px-3 py-2 text-center align-bottom shadow-[0_1px_0_0_#e2e8f0]"
                    style={{
                      width: ACCOUNT_COL_PX,
                      minWidth: ACCOUNT_COL_PX,
                      maxWidth: ACCOUNT_COL_PX,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort("account_name")}
                      className="mx-auto block w-full text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:text-gray-900"
                    >
                      <span className="inline-flex items-center justify-center gap-1">
                        Account <ArrowUpDown size={11} aria-hidden />
                      </span>
                      <span className="block font-normal normal-case tracking-normal text-gray-400">
                        ({sortedData.length} total)
                      </span>
                    </button>
                  </th>
                  {headerColumns.map(col => (
                    <th
                      key={col.id}
                      className="sticky top-0 z-30 border-l border-gray-200 bg-[#f1f5f9] px-2 py-2.5 text-center align-middle shadow-[0_1px_0_0_#e2e8f0]"
                    >
                      <button
                        type="button"
                        onClick={() => handleSort(col.sortField)}
                        title={`Sort by ${col.label}`}
                        className="mx-auto block w-full text-center text-[11px] font-semibold leading-tight text-gray-600 hover:text-gray-900"
                      >
                        {col.label}
                        {sortMarker(col.sortField)}
                      </button>
                    </th>
                  ))}
                  <th
                    className="sticky top-0 z-30 border-l border-gray-200 bg-[#f1f5f9] px-3 py-2.5 align-middle shadow-[0_1px_0_0_#e2e8f0]"
                    style={{
                      width: ACTION_COL_PX,
                      minWidth: ACTION_COL_PX,
                      maxWidth: ACTION_COL_PX,
                    }}
                  >
                    <span className="block text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Action
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedData.slice(0, visibleRowCount).map((account, rowIndex) => {
                  const accountId = String(account.account_id || "").trim();
                  const nativeRow = isDigitalocean
                    ? nativeRowsByAccountId[accountId]
                    : null;
                  // digitalocean: paint from xfactor view=table categoryScore
                  // (same as XfactorKpiMatrix + click modal), not Hawkeye digest.
                  const overallScore = (() => {
                    if (isDigitalocean && nativeRow) {
                      return nativeRow.hasData
                        ? (nativeRow.overall?.score ?? null)
                        : null;
                    }
                    if (!account.hasData) return null;
                    return Number.isFinite(account.overallScore)
                      ? account.overallScore
                      : null;
                  })();
                  const overallBubble = toMatrixBubble(
                    OVERALL_ID,
                    "Overall",
                    overallScore == null || !Number.isFinite(overallScore)
                      ? null
                      : overallScore,
                    overallScore == null || !Number.isFinite(overallScore)
                      ? null
                      : OVERALL_WEIGHT_SHARE,
                  );

                  const categoryBubbles = displayCategories.map(category => {
                    let numeric: number | null = null;
                    if (isDigitalocean && nativeRow) {
                      if (nativeRow.hasData) {
                        const nativeCat = nativeRow.categories.find(
                          c => c.id === category
                        );
                        const s = nativeCat?.score ?? null;
                        numeric =
                          s == null || !Number.isFinite(s) ? null : s;
                      }
                    } else {
                      const categoryScore = account.categoryScores?.find(
                        (cs: any) => cs.category === category,
                      );
                      const hasCategoryMetrics =
                        Array.isArray(categoryScore?.metrics) &&
                        categoryScore.metrics.length > 0;
                      const aiCatScore = account.aiCategoryScores?.[category];
                      const hasScore =
                        account.hasData &&
                        (hasCategoryMetrics ||
                          (typeof aiCatScore === "number" &&
                            Number.isFinite(aiCatScore)));
                      const score = hasScore
                        ? (categoryScore?.score ?? aiCatScore ?? null)
                        : null;
                      numeric =
                        score == null || !Number.isFinite(score)
                          ? null
                          : score;
                    }
                    const weightShare = shareOf(category);
                    return {
                      category,
                      score: numeric,
                      weightShare,
                      bubble: toMatrixBubble(
                        category,
                        getCategoryLabel(category),
                        numeric,
                        weightShare,
                      ),
                    };
                  });

                  const allBubbles = [
                    {
                      key: OVERALL_ID,
                      bubble: overallBubble,
                      category: OVERALL_ID,
                      score: overallBubble.score,
                      weightShare: OVERALL_WEIGHT_SHARE,
                      isOverall: true,
                    },
                    ...categoryBubbles.map(c => ({
                      key: c.category,
                      bubble: c.bubble,
                      category: c.category,
                      score: c.score,
                      weightShare: c.weightShare,
                      isOverall: false,
                    })),
                  ];

                  const summary = accountSummary(account);
                  const summaryOpen =
                    Boolean(summary) && isSummaryOpen(account.account_id, rowIndex);
                  const showBriefLine = summaryOpen && Boolean(summary);

                  return (
                    <React.Fragment key={account.account_id}>
                    <tr
                      className="border-t border-gray-100 hover:bg-gray-50/60"
                    >
                      <td
                        className={`sticky left-0 z-10 border-r border-gray-100 bg-white px-3 py-2 align-middle${
                          showBriefLine
                            ? " border-b border-slate-200"
                            : ""
                        }`}
                        rowSpan={showBriefLine ? 2 : undefined}
                        style={{
                          width: ACCOUNT_COL_PX,
                          minWidth: ACCOUNT_COL_PX,
                          maxWidth: ACCOUNT_COL_PX,
                        }}
                      >
                        <div className="flex items-center justify-start gap-1.5">
                          <label className="relative inline-flex shrink-0 cursor-pointer items-center justify-center">
                            <input
                              type="checkbox"
                              aria-label={`Select ${account.account_name} for comparison`}
                              checked={selectedCompareAccountIds.includes(
                                account.account_id,
                              )}
                              onChange={() =>
                                toggleCompareSelection(account.account_id)
                              }
                              className="peer sr-only"
                            />
                            <span className="h-4 w-4 rounded-[4px] border border-slate-300 bg-white shadow-sm transition-colors peer-checked:border-violet-600 peer-checked:bg-violet-600 peer-hover:border-slate-400" />
                            <svg
                              viewBox="0 0 16 16"
                              className="pointer-events-none absolute h-2.5 w-2.5 text-white opacity-0 transition-opacity peer-checked:opacity-100"
                              aria-hidden="true"
                            >
                              <path
                                d="M3.5 8.5L6.3 11.3L12.5 5.1"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </label>
                          {summary ? (
                            <button
                              type="button"
                              onClick={() =>
                                toggleSummary(account.account_id, summaryOpen)
                              }
                              aria-expanded={summaryOpen}
                              aria-controls={`kpi-summary-${account.account_id}`}
                              title={
                                summaryOpen ? "Hide summary" : "Show summary"
                              }
                              aria-label={
                                summaryOpen
                                  ? `Hide ${account.account_name} summary`
                                  : `Show ${account.account_name} summary`
                              }
                              className="shrink-0 text-gray-400 hover:text-gray-700"
                            >
                              {summaryOpen ? (
                                <ChevronDown size={14} aria-hidden />
                              ) : (
                                <ChevronRight size={14} aria-hidden />
                              )}
                            </button>
                          ) : null}
                          <div className="min-w-0 flex-1">
                            <div
                              className="block truncate text-left font-medium text-gray-900"
                              title={account.account_name}
                            >
                              {account.account_name}
                            </div>
                          </div>
                        </div>
                      </td>
                      {allBubbles.map((cell, i, all) => (
                        <td
                          key={cell.key}
                          className="px-1 py-1 text-center align-middle"
                          style={railStyle(i, all.length)}
                        >
                          <Bubble
                            bubble={cell.bubble}
                            waveIndex={i}
                            sizeLabel="Weight"
                            sizeValueText={
                              cell.isOverall
                                ? "100%"
                                : pctText(cell.weightShare)
                            }
                            onClick={() => {
                              if (cell.key === OVERALL_ID) {
                                handleOverallBubbleClick(account);
                              } else {
                                handleBubbleClick(
                                  account,
                                  cell.category,
                                  cell.score ?? 0,
                                  null,
                                );
                              }
                            }}
                          />
                        </td>
                      ))}
                      <td
                        className="whitespace-nowrap px-3 py-1 text-center align-middle"
                        style={{
                          width: ACTION_COL_PX,
                          minWidth: ACTION_COL_PX,
                          maxWidth: ACTION_COL_PX,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => openSituationCard(account)}
                          title={
                            account.hasData
                              ? "Situation Card"
                              : "No Account Health data for this account"
                          }
                          aria-label={
                            account.hasData
                              ? `Open ${account.account_name} situation card`
                              : `Situation Card unavailable for ${account.account_name}`
                          }
                          disabled={!account.hasData}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#ad46ff]/10 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
                          style={{ color: ACCENT }}
                        >
                          <SquarePlay size={22} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openFullBriefing(account)}
                          title={
                            account.hasData
                              ? "Full Briefing"
                              : "No Account Health data for this account"
                          }
                          aria-label={
                            account.hasData
                              ? `Open ${account.account_name} full briefing`
                              : `Full Briefing unavailable for ${account.account_name}`
                          }
                          disabled={!account.hasData || !isDigitalocean}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-[#ad46ff]/10 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
                          style={{ color: ACCENT }}
                        >
                          <ClipboardList size={20} />
                        </button>
                      </td>
                    </tr>
                    {showBriefLine && summary ? (
                      <tr
                        id={`kpi-summary-${account.account_id}`}
                        className="table-row account-brief-row"
                        aria-label={`${account.account_name} briefing summary`}
                      >
                        <td
                          colSpan={Math.max(1, headerColumns.length + 1)}
                          className="account-brief-line-cell"
                        >
                          <p
                            title={summary}
                            className="account-brief-line account-brief-line--full"
                          >
                            {summary}
                          </p>
                        </td>
                      </tr>
                    ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      {selectedCompareAccountIds.length > 0 &&
      !isCompareModalOpen &&
      situationAccount == null
        ? (() => {
            const strip = (
              <KpiCompareSelectionStrip
                placement={selectionStripHost ? "footer" : "overlay"}
                panelRef={selectionStripHost ? undefined : selectionPanelRef}
                selectedAccounts={selectedCompareAccounts}
                minCompareAccounts={MIN_COMPARE_ACCOUNTS}
                canCompare={
                  selectedCompareAccountIds.length >= MIN_COMPARE_ACCOUNTS
                }
                showSpiderViewHint={false}
                onCompare={handleCompareClick}
                onRemoveAccount={toggleCompareSelection}
                onClear={() => {
                  setSelectedCompareAccountIds([]);
                  onHighlightedAccountIdsChange?.([]);
                }}
                onSaveAsWatchlist={
                  onSaveAsWatchlist
                    ? () => onSaveAsWatchlist(selectedCompareAccountIds)
                    : undefined
                }
              />
            );
            return selectionStripHost
              ? createPortal(strip, selectionStripHost)
              : strip;
          })()
        : null}
      </div>

      <Dialog open={isCompareModalOpen} onOpenChange={setIsCompareModalOpen}>
        <DialogContent
          hideClose
          className="flex max-h-[92vh] w-[95vw] max-w-[980px] flex-col overflow-hidden rounded-[14px] border border-slate-200 bg-white p-0 shadow-2xl"
        >
          <SpiderCompareModalContent
            allAccounts={sortedData}
            selectedAccountIds={selectedCompareAccountIds}
            categoryOrder={displayCategories}
            compareColors={COMPARE_COLORS}
            onToggleAccount={toggleCompareSelection}
            onClose={() => setIsCompareModalOpen(false)}
            validationMessage={compareSelectionMessage}
          />
        </DialogContent>
      </Dialog>

      <KpiCompareMissingDataModal
        isOpen={isMissingKpiDataModalOpen}
        onClose={() => setIsMissingKpiDataModalOpen(false)}
        onRemoveAndCompare={handleRemoveAccountsWithoutDataAndCompare}
        accountNames={selectedAccountsWithoutKpiData.map(
          account => account.account_name
        )}
        allSelectedLackData={
          selectedCompareAccounts.length > 0 &&
          selectedAccountsWithoutKpiData.length ===
            selectedCompareAccounts.length
        }
      />

      {isDigitalocean && nativeDetail?.fromFullBriefing ? (
        <KpiFullBriefingModal
          open={Boolean(nativeDetail)}
          onClose={() => setNativeDetail(null)}
          accountName={nativeAccountName}
          accountId={nativeDetail?.accountId}
          briefing={nativeBriefing}
          loading={nativeBriefingLoading}
          error={nativeBriefingError}
        />
      ) : null}

      {isDigitalocean && nativeDetail && !nativeDetail.fromFullBriefing ? (
        <KpiCategoryDetailModal
          open
          onClose={() => setNativeDetail(null)}
          accountName={nativeAccountName}
          accountId={nativeDetail.accountId}
          briefing={nativeBriefing}
          loading={nativeBriefingLoading}
          error={nativeBriefingError}
          categoryId={nativeDetail.categoryId ?? "overall"}
          overallTitle="Overall (KPI Metrics)"
          onCategoryIdChange={categoryId =>
            setNativeDetail(s => (s ? { ...s, categoryId } : s))
          }
          onPrevAccount={
            nativeAccountIndex > 0
              ? () =>
                  setNativeDetail(s =>
                    s
                      ? {
                          ...s,
                          accountId: sortedData[nativeAccountIndex - 1]
                            .account_id,
                        }
                      : s
                  )
              : undefined
          }
          onNextAccount={
            nativeAccountIndex >= 0 &&
            nativeAccountIndex < sortedData.length - 1
              ? () =>
                  setNativeDetail(s =>
                    s
                      ? {
                          ...s,
                          accountId: sortedData[nativeAccountIndex + 1]
                            .account_id,
                        }
                      : s
                  )
              : undefined
          }
          canPrevAccount={nativeAccountIndex > 0}
          canNextAccount={
            nativeAccountIndex >= 0 &&
            nativeAccountIndex < sortedData.length - 1
          }
        />
      ) : null}

      <Modal
        open={situationAccount != null}
        onClose={() => {
          setSituationAccount(null);
          setSituationCardView("situation");
        }}
        width="max-w-5xl"
        title={
          <span>
            {situationCardView === "situation"
              ? "Situation Card"
              : "Executive Brief"}{" "}
            —{" "}
            <span style={{ color: ACCENT }}>
              {situationAccount?.accountName || "Unknown account"}
            </span>
          </span>
        }
        subtitle={
          formatIsoDateMonDayYear(kpiSituationDate) ||
          kpiSituationDate ||
          undefined
        }
        actions={
          <button
            type="button"
            onClick={() =>
              setSituationCardView(v =>
                v === "situation" ? "brief" : "situation"
              )
            }
            disabled={
              situationCardView === "situation" &&
              (!kpiSituationData || kpiSituationLoading)
            }
            className="inline-flex shrink-0 items-center gap-1.5 self-center whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            style={{ backgroundColor: ACCENT }}
          >
            {situationCardView === "situation" ? (
              <>
                Executive Brief <ArrowRight size={13} aria-hidden />
              </>
            ) : (
              <>
                <ArrowLeft size={13} aria-hidden /> Situation Card
              </>
            )}
          </button>
        }
      >
        {situationAccount ? (
          situationCardView === "situation" ? (
            <KpiSituationCard
              accountName={situationAccount.accountName}
              data={kpiSituationData}
              loading={kpiSituationLoading}
            />
          ) : (
            <KpiExecutiveBrief
              data={kpiSituationData}
              loading={kpiSituationLoading}
            />
          )
        ) : null}
      </Modal>
    </>
  );
};
